import { SavingsDepositTxOverview } from '../types'
import { filterRelatedCampaigns } from './filterRelatedCampaigns'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { TransferFromUserFormNormalizedData } from '@/features/dialogs/common/logic/transfer-from-user/form'
import { SavingsTokenRepository } from '@/features/savings/common/useSavingsTokenRepository'
import { SavingsAccount } from '@/features/savings/logic/savings-converters/types'
import { getAccountDisplayedUnderlyingToken } from '@/features/savings/utils/getAccountDisplayedUnderlyingToken'
import { OngoingCampaignWithRestrictedCountry } from '@/features/spark-rewards/data/useOngoingCampaignsQuery'

export interface CreateTxOverviewParams {
  formValues: TransferFromUserFormNormalizedData
  savingsAccount: SavingsAccount
  tokenRepository: SavingsTokenRepository
  chainId: number
  accountName: string
  ongoingCampaigns: OngoingCampaignWithRestrictedCountry[] | undefined
}
export function createTxOverview({
  formValues,
  savingsAccount,
  tokenRepository,
  chainId,
  accountName,
  ongoingCampaigns,
}: CreateTxOverviewParams): SavingsDepositTxOverview {
  const providedBy =
    savingsAccount.savingsToken.symbol === TokenSymbol('sUSDC') || savingsAccount.savingsToken.symbol.startsWith('sp')
      ? 'spark'
      : 'sky'
  const relatedCampaigns = filterRelatedCampaigns({
    ongoingCampaigns,
    savingsAccount,
    chainId,
  })

  const savingsToken = tokenRepository.findOneBySymbol(savingsAccount.savingsToken.symbol)
  const displayedTokenDefinition = getAccountDisplayedUnderlyingToken({ account: savingsAccount, chainId })
  const underlyingToken = tokenRepository.findOneBySymbol(displayedTokenDefinition.symbol)

  // the value is normalized, so assuming 1 to 1 conversion rate for USDC
  // value denominated in DAI equals to value denominated in USDC
  const value = formValues.value
  if (value.eq(0)) {
    return { status: 'no-overview', providedBy, relatedCampaigns, savingsToken }
  }

  const stableEarnRate = value.times(savingsAccount.converter.apy)

  const underlyingTokenToDeposit = value
  const savingsTokenBalance = tokenRepository.findOneBalanceOrZero(savingsToken.symbol)
  const underlyingTokenDepositedBefore = savingsAccount.converter.convertToAssets({ shares: savingsTokenBalance })
  const underlyingTokenDepositedAfter = underlyingTokenDepositedBefore.plus(underlyingTokenToDeposit)

  return {
    status: 'success',
    providedBy,
    APY: savingsAccount.converter.apy,
    stableEarnRate,
    underlyingToken,
    savingsToken,
    accountName,
    underlyingTokenToDeposit,
    underlyingTokenDepositedBefore,
    underlyingTokenDepositedAfter,
    relatedCampaigns,
  }
}
